import z from "zod";
import type { AgentServer } from "@/agent/server/class";
import {
  type PluginAccessor,
  type PluginConfig,
  type PluginContext,
  type PluginContextListener,
  type PluginDefinition,
  type PluginDependenciesAccessor,
  type PluginEvent,
  type PluginEventSource,
  type PluginEventsListener,
  type PluginEventsSelector,
  type PluginHandlerDefinition,
  type PluginInternalEvent,
  pluginEventInputSchema,
} from "@/plugins/server/types";
import { AsyncQueue } from "@/shared/async-queue";
import { canon, type SerializableValue } from "@/shared/canon";
import { deepClone } from "@/shared/deep-clone";
import { lifeError } from "@/shared/error";
import { toMethodName } from "@/shared/method-name";
import * as op from "@/shared/operation";
import { newId } from "@/shared/prefixed-id";
import { RollingBuffer } from "@/shared/rolling-buffer";
import type { Any, Todo } from "@/shared/types";
import type { TelemetryClient } from "@/telemetry/clients/base";
import { createTelemetryClient } from "@/telemetry/clients/node";

export class PluginServer<const PluginDef extends PluginDefinition> {
  readonly def: PluginDef;
  readonly agent: AgentServer;
  readonly config: PluginConfig<PluginDef["config"], "output">;
  context: PluginContext<PluginDef["context"], "output">;
  readonly telemetry: TelemetryClient;
  readonly queue = new AsyncQueue<PluginEvent<PluginDef["events"], "output">>();
  readonly eventsListeners = new Map<string, PluginEventsListener>();
  readonly contextListeners = new Map<string, PluginContextListener>();
  readonly streamHandlersQueues: AsyncQueue<PluginEvent<PluginDef["events"], "output">>[] = [];
  readonly externalInterceptHandlers: {
    dependent: PluginServer<PluginDefinition>;
    handler: PluginHandlerDefinition;
  }[] = [];
  readonly #internalAccessor: PluginAccessor<
    PluginDef,
    { type: "server"; name: PluginDef["name"] }
  >;
  readonly #waitProcessingResolvers = new Map<string, Set<() => void>>();
  readonly #waitResultResolvers = new Map<
    string,
    Map<string, Set<(result: op.OperationResult<unknown>) => void>>
  >();
  readonly #waitProcessingHistory = new RollingBuffer<string>(1000);
  readonly #waitResultHistory = new RollingBuffer<{
    eventId: string;
    handlerName: string;
    result: op.OperationResult<unknown>;
  }>(1000);
  readonly #handlersStates = new Map<string, unknown>();

  constructor({
    agent,
    definition,
    config,
    context = {},
  }: {
    agent: AgentServer;
    definition: PluginDef;
    config: PluginConfig<PluginDef["config"], "input">;
    context: SerializableValue;
  }) {
    this.def = definition;
    this.agent = agent;

    // Validate config
    const { error: errConfig, data: parsedConfig } = definition.config.schema.safeParse(config);
    if (errConfig)
      throw lifeError({
        code: "Validation",
        message: `Invalid config provided to plugin server '${definition.name}'.`,
        cause: errConfig,
      });
    this.config = parsedConfig as PluginConfig<PluginDef["config"], "output">;

    // Validate context
    const { error: errContext, data: parsedContext } = definition.context.schema.safeParse(context);
    if (errContext)
      throw lifeError({
        code: "Validation",
        message: `Invalid context provided to plugin server '${definition.name}'.`,
        cause: errContext,
      });
    this.context = parsedContext as PluginContext<PluginDef["context"], "output">;

    // Initialize telemetry
    this.telemetry = createTelemetryClient("plugin.server", {
      agentId: agent.id,
      agentSha: agent.sha,
      agentName: agent.def.name,
      agentConfig: agent.config,
      transportProviderName: agent.config.transport.provider,
      llmProviderName: agent.config.models.llm.provider,
      sttProviderName: agent.config.models.stt.provider,
      eouProviderName: agent.config.models.eou.provider,
      ttsProviderName: agent.config.models.tts.provider,
      vadProviderName: agent.config.models.vad.provider,
      pluginName: definition.name,
      pluginServerConfig: this.config,
    });

    // Initialize client RPC interface
    this.#initClientRPC();

    // Instantiate an internal accessor instance to be used outside of handlers
    const [errAccessor, accessor] = this.getAccessor({
      type: "server",
      name: this.def.name,
    });
    if (errAccessor) throw errAccessor;
    this.#internalAccessor = accessor;
  }

  /**
   * Produces an accessor object, exposing methods to interact safely with the plugin
   * from a given source (plugin, handler, client).
   * @param source - The source accessing the plugin.
   * @param handlerAccess - The access mode for the handler.
   * @returns An accessor object.
   */
  getAccessor<Source extends PluginEventSource, HandlerAccess extends "read" | "write" = "read">(
    source: Source,
    handlerAccess?: HandlerAccess,
  ) {
    // config
    const [errClone, clonedConfig] = op.attempt(() => deepClone(this.config));
    if (errClone) return op.failure(errClone);

    // context.onChange()
    const contextOnChange = ((selector, callback) => {
      const id = newId("listener");
      this.contextListeners.set(id, { id, callback, selector });
      return op.success(() => this.contextListeners.delete(id));
    }) satisfies PluginAccessor<PluginDef, { type: "server"; name: string }>["context"]["onChange"];

    // context.get()
    const contextGet = (() => op.attempt(() => deepClone(this.context))) satisfies PluginAccessor<
      PluginDef,
      { type: "server"; name: string }
    >["context"]["get"];

    // context.set()
    const contextSet = ((valueOrUpdater) => {
      // Snapshot the old context value
      const [errOld, oldContext] = op.attempt(() => deepClone(this.context));
      if (errOld) return op.failure(errOld);

      // Set the new context value
      if (typeof valueOrUpdater === "function") this.context = valueOrUpdater(oldContext);
      else this.context = valueOrUpdater;

      Promise.all([
        // Notify context change listeners
        Array.from(this.contextListeners.values()).map(async (listener) => {
          try {
            const newSelectedValue = listener.selector(this.context) as SerializableValue;
            const oldSelectedValue = listener.selector(oldContext) as SerializableValue;
            // Only call if value actually changed
            const [errEqual, equal] = canon.equal(newSelectedValue, oldSelectedValue);
            if (errEqual) return op.failure(errEqual);
            if (equal) await listener.callback(deepClone(this.context), deepClone(oldContext));
          } catch (error) {
            this.telemetry.log.error({
              message: `Error while notifying context listeners in plugin '${this.def.name}'.`,
              error,
            });
          }
        }),
        // Send new context value updates via RPC
        this.agent.transport.call({
          name: `plugin.${this.def.name}.context.changed`,
          schema: {
            input: z.object({ value: z.any(), timestamp: z.number() }),
          },
          input: { value: this.context, timestamp: Date.now() },
        }),
      ]);

      return op.success();
    }) satisfies PluginAccessor<
      PluginDef,
      { type: "server"; name: string },
      "write"
    >["context"]["set"];

    // events.emit()
    const eventsEmit = ((event) => {
      // Validate event shape
      const { error: errEvent, data: parsedEvent } = pluginEventInputSchema.safeParse(event);
      if (errEvent)
        return op.failure({
          code: "Validation",
          message: "Invalid event shape for event.",
          cause: errEvent,
        });

      // If not internal, ensure the event is not an internal event
      const isInternal = source.type === "server" && source.name === this.def.name;
      if (!isInternal && parsedEvent.name.startsWith("plugin."))
        return op.failure({
          code: "Validation",
          message: "Internal events cannot be emitted from outside the plugin.",
        });

      // Ensure the event type exists
      const eventDef = this.def.events?.find((e) => e.name === parsedEvent.name);
      if (!eventDef)
        return op.failure({
          code: "Validation",
          message: `Event of type '${parsedEvent.name}' not found.`,
        });

      // Validate the event data
      let parsedData: unknown | null = null;
      if (eventDef.dataSchema) {
        const { error: errData, data } = eventDef.dataSchema.safeParse(parsedEvent.data);
        parsedData = data;
        if (errData)
          return op.failure({
            code: "Validation",
            message: `Invalid event data shape for '${parsedEvent.name}' event.`,
            cause: errData,
          });
      }

      // Generate an id for the event
      const outputEvent: PluginEvent<PluginDef["events"], "output"> = {
        id: newId("event"),
        name: parsedEvent.name,
        urgent: parsedEvent.urgent,
        data: parsedData as Any,
        created: { at: Date.now(), by: source },
        edited: false,
        dropped: false,
        contextChanges: [],
      };

      // Append to queue
      if (outputEvent.urgent) this.queue.pushFirst(outputEvent);
      else this.queue.push(outputEvent);

      // Return the id
      return op.success(outputEvent.id);
    }) satisfies PluginAccessor<PluginDef, { type: "server"; name: string }>["events"]["emit"];

    // events.on()
    const eventsOn = ((selector, callback, includeDropped = false) => {
      const id = newId("listener");
      this.eventsListeners.set(id, { id, callback, selector, includeDropped });
      return op.success(() => this.eventsListeners.delete(id));
    }) satisfies PluginAccessor<PluginDef, { type: "server"; name: string }>["events"]["on"];

    // events.once()
    const eventsOnce = ((selector, callback, includeDropped = false) => {
      const [errOn, unsubscribe] = eventsOn(
        selector,
        async (event) => {
          unsubscribe?.();
          await callback(event);
        },
        includeDropped,
      );
      if (errOn) return op.failure(errOn);
      return op.success(unsubscribe);
    }) satisfies PluginAccessor<PluginDef, { type: "server"; name: string }>["events"]["once"];

    // events.waitForProcessing()
    const eventsWaitForProcessing = (async (eventId) => {
      // Holds resolver, and a cleanup function
      let resolver: (() => void) | undefined;
      const cleanupResolver = () => {
        if (!resolver) return;
        this.#waitProcessingResolvers.get(eventId)?.delete(resolver);
        if (this.#waitProcessingResolvers.get(eventId)?.size === 0)
          this.#waitProcessingResolvers.delete(eventId);
      };

      try {
        // If the event was already processed recently, return success immediately
        if (this.#waitProcessingHistory.get().includes(eventId)) return op.success();

        // Else register a resolver, and wait until resolve or timeout
        const resolverPromise = new Promise<op.OperationResult<void>>(
          (r) => (resolver = () => void r(op.success())),
        );
        if (!resolver) throw new Error("Resolver not found. Shouldn't happen.");
        if (!this.#waitProcessingResolvers.has(eventId))
          this.#waitProcessingResolvers.set(eventId, new Set());
        this.#waitProcessingResolvers.get(eventId)?.add(resolver);

        const timeoutPromise = new Promise<op.OperationResult<void>>((resolve) => {
          setTimeout(() => {
            resolve(
              op.failure({
                code: "Timeout",
                message: `Waiting for event '${eventId}' processing timed out.`,
                isPublic: true,
              }),
            );
          }, 15_000);
        });

        const [err] = await Promise.race([resolverPromise, timeoutPromise]);

        // Return the error if any
        if (err) return op.failure(err);

        // Append the event id to the processing history
        this.#waitProcessingHistory.add(eventId);

        // Return success
        return op.success();
      } catch (error) {
        return op.failure({ code: "Unknown", cause: error });
      } finally {
        cleanupResolver();
      }
    }) satisfies PluginAccessor<
      PluginDef,
      { type: "server"; name: string }
    >["events"]["waitForProcessing"];

    // events.waitForResult()
    const eventsWaitForResult = (async (eventId, handlerName) => {
      // Holds resolver, and a cleanup function
      let resolver: ((result: op.OperationResult<Any>) => void) | undefined;
      const cleanupResolver = () => {
        if (!resolver) return;
        this.#waitResultResolvers.get(eventId)?.get(handlerName)?.delete(resolver);
        if (this.#waitResultResolvers.get(eventId)?.get(handlerName)?.size === 0)
          this.#waitResultResolvers.get(eventId)?.delete(handlerName);
        if (this.#waitResultResolvers.get(eventId)?.size === 0)
          this.#waitResultResolvers.delete(eventId);
      };

      try {
        // If the event was already processed recently, return result immediately
        const historyItem = this.#waitResultHistory
          .get()
          .find((h) => h.eventId === eventId && h.handlerName === handlerName);
        if (historyItem) return historyItem.result;

        // Else register a resolver and wait until resolve or timeout
        const resolverPromise = new Promise<op.OperationResult<Any>>((r) => (resolver = r));
        if (!resolver) throw new Error("Resolver not found. Shouldn't happen.");
        if (!this.#waitResultResolvers.has(eventId))
          this.#waitResultResolvers.set(eventId, new Map());
        if (!this.#waitResultResolvers.get(eventId)?.get(handlerName))
          this.#waitResultResolvers.get(eventId)?.set(handlerName, new Set());
        this.#waitResultResolvers.get(eventId)?.get(handlerName)?.add(resolver);

        const timeoutPromise = new Promise<op.OperationResult<void>>((resolve) => {
          setTimeout(() => {
            resolve(
              op.failure({
                code: "Timeout",
                message: `Waiting for event '${eventId}' result timed out.`,
                isPublic: true,
              }),
            );
          }, 15_000);
        });

        const result = await Promise.race([resolverPromise, timeoutPromise]);

        // Append the result to the history
        this.#waitResultHistory.add({ eventId, handlerName, result });

        // Return result
        return result;
      } catch (error) {
        return op.failure({ code: "Unknown", cause: error });
      } finally {
        cleanupResolver();
      }
    }) satisfies PluginAccessor<
      PluginDef,
      { type: "server"; name: string }
    >["events"]["waitForResult"];

    if (source.type === "handler") {
      return op.success({
        config: clonedConfig,
        context: {
          get: contextGet,
          ...(handlerAccess === "write" ? { set: contextSet } : {}),
          onChange: contextOnChange,
        },
        events: {
          emit: eventsEmit,
          waitForResult: eventsWaitForResult,
          waitForProcessing: eventsWaitForProcessing,
        },
      } as unknown as PluginAccessor<PluginDef, Source, HandlerAccess>);
    }

    if (source.type === "server") {
      return op.success({
        config: clonedConfig,
        context: {
          get: contextGet,
          onChange: contextOnChange,
        },
        events: {
          emit: eventsEmit,
          on: eventsOn,
          once: eventsOnce,
          waitForResult: eventsWaitForResult,
          waitForProcessing: eventsWaitForProcessing,
        },
      } as unknown as PluginAccessor<PluginDef, Source, HandlerAccess>);
    }

    if (source.type === "client") {
      return op.success({
        config: clonedConfig,
        context: {
          get: contextGet,
          onChange: contextOnChange,
        },
        events: {
          emit: eventsEmit,
          on: eventsOn,
          once: eventsOnce,
          waitForResult: eventsWaitForResult,
          waitForProcessing: eventsWaitForProcessing,
        },
      } as unknown as PluginAccessor<PluginDef, Source, HandlerAccess>);
    }

    return op.failure({
      code: "NotFound",
      message: `Unknown source type '${(source as { type: string }).type}'.`,
    });
  }

  /**
   * Produces a map of dependencies' accessors, see `getAccessor()`.
   * @param source - The source accessing the dependencies.
   * @returns A map of dependencies' accessors.
   */
  getDependenciesAccessors<Source extends PluginEventSource>(source: Source) {
    const dependenciesAccessors = {} as PluginDependenciesAccessor<
      PluginDef["dependencies"],
      Source
    >;
    for (const dependencyDef of this.def.dependencies ?? []) {
      const depServer = this.agent.plugins?.[toMethodName(dependencyDef.name)];
      if (!depServer)
        return op.failure({
          code: "NotFound",
          message: `Failed to obtain server instance for dependency plugin '${dependencyDef.name}'. Shouldn't happen.`,
        });
      const [errAccessor, accessor] = depServer.getAccessor(source);
      if (errAccessor) return op.failure(errAccessor);
      // @ts-expect-error
      dependenciesAccessors[dependencyDef.name] = accessor;
    }

    return op.success(dependenciesAccessors);
  }

  async start() {
    return await this.telemetry.trace(`plugin.${this.def.name}.start()`, async () => {
      // 1. Initialize 'stream()' handlers sub-queues
      const streamHandlers = this.def.handlers.filter((h) => h.mode === "stream");
      for (const handler of streamHandlers) {
        const queue = new AsyncQueue<PluginEvent<PluginDef["events"], "output">>();
        this.streamHandlersQueues.push(queue);
        (async () => {
          for await (const event of queue) await this.#executeHandler(handler, event);
        })();
      }

      // 2. Register 'intercept' handlers onto dependency plugins
      const interceptHandlers = this.def.handlers.filter((h) => h.mode === "intercept");
      for (const handler of interceptHandlers) {
        for (const dependencyDef of this.def.dependencies ?? []) {
          const dependency = this.agent.plugins?.[dependencyDef.name];
          if (!dependency) {
            this.telemetry.log.error(
              lifeError({
                code: "NotFound",
                message: `Failed to obtain server instance for dependency plugin '${dependencyDef.name}'. Shouldn't happen.`,
              }),
            );
            continue;
          }
          dependency.externalInterceptHandlers.push({
            dependent: this as unknown as PluginServer<PluginDefinition>,
            handler,
          });
        }
      }

      // 3. Run the synchronous event loop with 'block' handlers and remote 'intercept' handlers
      (async () => {
        for await (const event of this.queue) {
          try {
            // Run external 'intercept' handlers
            for (const { handler, dependent } of this.externalInterceptHandlers) {
              // biome-ignore lint/performance/noAwaitInLoops: sequential execution expected here
              await this.#executeHandler(handler, event, dependent, (result) => {
                if (result.type === "drop") {
                  if (event.dropped)
                    event.dropped = {
                      at: Date.now(),
                      by: {
                        plugin: this.def.name,
                        handler: handler.name,
                      },
                      reason: result.reason,
                    };
                } else if (result.type === "next") {
                  const [errClone, oldData] = op.attempt(() => deepClone(event.data));
                  if (errClone) return op.failure(errClone);
                  if (!event.edited) event.edited = [];
                  event.edited.push({
                    at: Date.now(),
                    by: {
                      plugin: this.def.name,
                      handler: handler.name,
                    },
                    reason: result.reason,
                    dataBefore: oldData,
                    dataAfter: result.data,
                  });
                  event.data = result.data;
                }
              });
              if (event.dropped) break;
            }

            // If the event was not dropped
            if (!event.dropped) {
              // Run the 'block' handlers
              const blockHandlers = this.def.handlers.filter((h) => h.mode === "block");
              for (const handler of blockHandlers) {
                // Take a snapshot of the context data before executing the handler
                const [errOld, oldContext] = op.attempt(() => deepClone(this.context));
                if (errOld) {
                  this.telemetry.log.error({ error: errOld });
                  continue;
                }
                // Execute the handler
                // biome-ignore lint/performance/noAwaitInLoops: sequential execution expected here
                await this.#executeHandler(handler, event);
                // If the context has changed, record the change
                const [errEqual, equal] = canon.equal(
                  this.context as SerializableValue,
                  oldContext as SerializableValue,
                );
                if (errEqual) {
                  this.telemetry.log.error({ error: errEqual });
                  continue;
                }
                if (!equal) {
                  if (!event.contextChanges) event.contextChanges = [];
                  event.contextChanges.push({
                    at: Date.now(),
                    byHandler: handler.name,
                    valueBefore: oldContext,
                    valueAfter: this.context,
                  });
                }
              }
              // Feed the 'stream' handlers' queues
              for (const queue of this.streamHandlersQueues) queue.push(event);
            }

            // Notify events listeners
            await Promise.all(
              Array.from(this.eventsListeners.values()).map(
                async ({ id, callback, selector, includeDropped }) => {
                  // If the event matches the listener selector
                  if (this.#eventMatchesSelector(event, selector)) {
                    // And the event was not dropped or the listener includes dropped events
                    if (!event.dropped || includeDropped) {
                      // If remote, send the event via RPC
                      if (callback === "remote") {
                        const [err] = await this.agent.transport.call({
                          name: `plugin.${this.def.name}.events.callback`,
                          schema: {
                            input: z.object({
                              listenerId: z.string(),
                              event: pluginEventInputSchema,
                            }),
                          },
                          input: { listenerId: id, event },
                        });
                        if (err)
                          this.telemetry.log.error({
                            message: `Error while steaming event to remote listener in plugin '${this.def.name}'.`,
                            error: err,
                          });
                      }
                      // Else, call the local callback
                      else await callback(event);
                    }
                  }
                },
              ),
            );

            // Resolve processing waiters
            const waiters = this.#waitProcessingResolvers.get(event.id) ?? [];
            for (const resolver of waiters) resolver();
          } catch (error) {
            this.telemetry.log.error({
              message: `Unknown error while executing event in plugin '${this.def.name}'.`,
              error,
            });
          }
        }
      })();

      // 4. Start the queue (send the 'plugin.start' event)
      const [errEmit, eventId] = this.#emitInternalEvent({
        name: "plugin.start",
        data: { isRestart: this.agent.isRestart, restartCount: 0 },
      });
      if (errEmit) return op.failure(errEmit);
      const [errWaitForResult] = await this.#internalAccessor.events.waitForProcessing(eventId);
      if (errWaitForResult) return op.failure(errWaitForResult);

      return op.success();
    });
  }

  async stop() {
    return await this.telemetry.trace(`plugin.${this.def.name}.stop()`, async () => {
      // 1. Send the 'plugin.stop' event at the front of the queue
      const [errEmit, eventId] = this.#emitInternalEvent({ name: "plugin.stop", urgent: true });
      if (errEmit) return op.failure(errEmit);
      const [errWaitForResult] = await this.#internalAccessor.events.waitForProcessing(eventId);
      if (errWaitForResult) return op.failure(errWaitForResult);

      // 2. Stop the main queue and 'stream' handlers queues
      this.queue.stop();
      for (const queue of this.streamHandlersQueues) queue.stop();

      // 3. Clear handlers state
      this.#handlersStates.clear();

      // 4. Return that the plugin was stopped successfully
      return op.success();
    });
  }

  #getHandlerState(
    handler: PluginDef["handlers"][number],
    accessor: PluginAccessor<
      PluginDef,
      { type: "handler"; plugin: string; handler: string; event: string }
    >,
  ) {
    const savedState = this.#handlersStates.get(handler.name);
    if (savedState) return savedState;
    const initialState =
      typeof handler.state === "function"
        ? handler.state({ config: accessor.config })
        : (handler.state ?? {});
    this.#handlersStates.set(handler.name, initialState);
    return initialState;
  }

  #emitInternalEvent(event: PluginInternalEvent<"input">) {
    // Any is used here, because emit() type excludes internal events
    const [errEmit, eventId] = this.#internalAccessor.events.emit(event as Any);
    if (errEmit) return op.failure(errEmit);
    return op.success(eventId);
  }

  async #executeHandler<Event extends PluginEvent<PluginDef["events"], "output">>(
    handler: PluginDef["handlers"][number],
    event: Event,
    dependency?: PluginServer<PluginDefinition>,
    onInterceptResult?: (
      result:
        | { type: "drop"; reason: string }
        | { type: "next"; data: Event["data"]; reason: string },
    ) => void,
  ) {
    // Run the handler onEvent function
    const result = await (async () => {
      const [errClone, clonedEvent] = op.attempt(() => deepClone(event));
      if (errClone) return op.failure(errClone);
      const handlerAccess = handler.mode === "block" ? "write" : "read";
      const [errAccessor, accessor] = this.getAccessor(
        {
          type: "handler",
          plugin: this.def.name,
          handler: handler.name,
          event: event.name,
        },
        handlerAccess,
      );
      if (errAccessor) return op.failure(errAccessor);
      const state = this.#getHandlerState(handler, accessor);
      const [errDependency, dependencyAccessor] = dependency
        ? dependency.getAccessor({ type: "server", name: dependency.def.name })
        : op.success({});
      if (errDependency) return op.failure(errDependency);
      const [errDependencies, dependenciesAccessor] = this.getDependenciesAccessors({
        type: "handler",
        plugin: this.def.name,
        handler: handler.name,
        event: event.name,
      });
      if (errDependencies) return op.failure(errDependencies);
      const drop = (reason: string) => onInterceptResult?.({ type: "drop", reason });
      const next = (data: PluginEvent<PluginDef["events"], "output">["data"], reason: string) =>
        onInterceptResult?.({ type: "next", data, reason });
      return await this.telemetry.trace(
        `plugin.${this.def.name}.handler.${handler.name}`,
        async (span) =>
          op.attempt(
            async () =>
              await handler.onEvent({
                event: clonedEvent,
                plugin: op.toPublic(accessor),
                state,
                telemetry: span,
                agent: op.toPublic(this.agent),
                ...(handler.mode === "intercept"
                  ? { drop, next, dependency: dependencyAccessor }
                  : { dependencies: dependenciesAccessor }),
              } as Todo),
          ),
      );
    })();

    // Log the error if any, and emit a plugin.error event
    if (result[0]) {
      this.telemetry.log.error({
        message: `Error while executing ${handler.mode} handler '${handler.name}' in plugin '${this.def.name}'.`,
        error: result[0],
      });
      this.#emitInternalEvent({ name: "plugin.error", data: { error: result[0], event } });
    }

    // Resolve result waiters
    const waiters = this.#waitResultResolvers.get(event.id)?.get(handler.name) ?? [];
    for (const resolver of waiters) resolver(result);
  }

  #initClientRPC() {
    const [errAccessor, accessor] = this.getAccessor({
      type: "client",
      name: this.def.name,
    });
    if (errAccessor) return op.failure(errAccessor);

    // Expose context via RPC
    this.agent.transport.register({
      name: `plugin.${this.def.name}.context.get`,
      schema: { output: z.object().loose() },
      execute: () => op.success({ value: this.context, timestamp: Date.now() }),
    });

    // Expose 'events.emit()' via RPC
    this.agent.transport.register({
      name: `plugin.${this.def.name}.events.emit`,
      schema: {
        input: pluginEventInputSchema,
        output: z.object({ id: z.string() }),
      },
      execute: async (input) => {
        const [err, id] = await accessor.events.emit(input as Any);
        if (err) return op.failure(err);
        return op.success({ id });
      },
    });

    // Setup events subscriptions via RPC
    this.agent.transport.register({
      name: `plugin.${this.def.name}.events.subscribe`,
      schema: {
        input: z.object({
          listenerId: z.string(),
          selector: z.any(),
          includeDropped: z.boolean().prefault(false),
        }),
      },
      execute: (input) => {
        const { listenerId, selector, includeDropped } = input;
        this.eventsListeners.set(listenerId, {
          id: listenerId,
          callback: "remote",
          selector,
          includeDropped,
        });
        return op.success();
      },
    });
    this.agent.transport.register({
      name: `plugin.${this.def.name}.events.unsubscribe`,
      schema: { input: z.object({ listenerId: z.string() }) },
      execute: (input) => {
        const { listenerId } = input;
        this.eventsListeners.delete(listenerId);
        return op.success();
      },
    });

    // Expose events waitForProcessing via RPC
    this.agent.transport.register({
      name: `plugin.${this.def.name}.events.waitForProcessing`,
      schema: { input: z.object({ eventId: z.string() }) },
      execute: async (input) => await accessor.events.waitForProcessing(input.eventId),
    });
    this.agent.transport.register({
      name: `plugin.${this.def.name}.events.waitForResult`,
      schema: {
        input: z.object({ eventId: z.string(), handlerName: z.string() }),
        output: z.object().loose(),
      },
      execute: async (input) =>
        (await accessor.events.waitForResult(
          input.eventId,
          input.handlerName,
        )) as op.OperationResult<Record<string, unknown>>,
    });
  }

  #eventMatchesSelector(
    event: PluginEvent<PluginDef["events"], "output">,
    selector: PluginEventsSelector<PluginDef["events"]>,
  ) {
    const isAllSelector = selector === "*";
    const isArraySelectorAndIncludesEvent =
      Array.isArray(selector) && selector.includes(event.name);
    const isObjectSelectorAndIncludesEvent =
      typeof selector === "object" &&
      "include" in selector &&
      (selector.include === "*" || selector.include.includes(event.name));
    const isObjectSelectorAndExcludesEvent =
      typeof selector === "object" &&
      "exclude" in selector &&
      selector.exclude?.includes(event.name);
    return (
      isAllSelector ||
      isArraySelectorAndIncludesEvent ||
      (isObjectSelectorAndIncludesEvent && !isObjectSelectorAndExcludesEvent)
    );
  }
}
